import { Box, Button, Text } from '@chakra-ui/react';
import { useContext, useEffect, useRef, useState } from 'react';
import MapContext from '../../context/MapContext';
import { Icon } from '@blueprintjs/core';
import { v4 as uuidv4 } from 'uuid';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import UIContext from '../../context/UIContext';
import { useHistory } from 'react-router-dom';
import * as turf from "@turf/turf";

const devMode = import.meta.env.MODE;
const token = import.meta.env.VITE_VECTOR_CHARTS_API_TOKEN;

const MenuButton = ({ children, onClick, icon, color }) => {
    return (
        <Button
            variant="unstyled"
            fontWeight="400"
            pl="10px"
            pr="10px"
            fontSize="14px"
            color={color || "black"}
            leftIcon={
                <Text mb="0px" color="rgba(0,0,0,0.4)">
                    <Icon icon={icon} />
                </Text>
            }
            _hover={{
                bg: 'rgba(0,0,0,0.05)',
            }}
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            onClick={onClick}
        >
            {children}
        </Button>
    );
};
export function MapContextMenu() {
    const { measureState, rulerControlRef, mapRef, getTileCoordsForPosition, style, styleSettings, contextMenuLocation, setContextMenuLocation, markers, setMarkers, reportIssueState, setReportIssueState, showUserReports } =
        useContext(MapContext);
    const { searchQuery, setSearchQuery, routeInputWaypoints, setRouteResults, setRouteInputWaypoints } = useContext(UIContext);

    const history = useHistory();

    const containerRef = useRef();
    const windowSize = useWindowDimensions();
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!contextMenuLocation) {
            return;
        }
        setContainerSize({
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight,
        });
    }, [contextMenuLocation]);
    const margin = 10;

    if (!contextMenuLocation) {
        return;
    }
    

    return (
        <Box
            position="absolute"
            ref={containerRef}
            top={Math.min(contextMenuLocation?.y, windowSize.height - containerSize.height - margin)}
            left={Math.min(contextMenuLocation?.x, windowSize.width - containerSize.width - margin)}
            zIndex={20}
            display="flex"
            flexDir="column"
            justifyContent="stretch"
            alignItems="stretch"
            bg="white"
            borderRadius="4px"
            border="1px solid rgba(0,0,0,0.1)"
            shadow="md"
        >
            <MenuButton
                icon="clipboard"
                onClick={(e) => {
                    navigator.clipboard.writeText(
                        `${contextMenuLocation?.latitude.toFixed(6)}, ${contextMenuLocation?.longitude.toFixed(6)}`
                    );
                    setContextMenuLocation(null);
                }}
            >
                {contextMenuLocation?.latitude.toFixed(6)}, {contextMenuLocation?.longitude.toFixed(6)}
            </MenuButton>
            {devMode && (
                <MenuButton
                icon="clipboard"
                onClick={(e) => {
                    const {
                        x,y,z 
                    } = getTileCoordsForPosition(Math.floor(mapRef.current.getZoom()), contextMenuLocation?.latitude, contextMenuLocation?.longitude);
                    const tileUrl = `${import.meta.env.VITE_ENC_API_URL_PREFIX}/api/v2/tiles/enc-v2/${z}/${x}/${y}.mvt?token=${token}`;
                    navigator.clipboard.writeText(tileUrl);
                    setContextMenuLocation(null);
                }}
                >
                Copy Tile URL
                </MenuButton>
            )}
            <Box w="100%" h="1px" bg="rgba(0,0,0,0.1)"></Box>
            <MenuButton onClick={() => {
                const bounds = mapRef.current.getBounds();
                console.log(bounds);
                const point1 = turf.point([bounds._ne.lng, bounds._ne.lat]);
                const point2 = turf.point([bounds._sw.lng, bounds._sw.lat]);
                const searchRadius = (turf.distance(point1, point2, {
                    units: "meters"
                })) / 20.0;
                console.log(searchRadius);

                setSearchQuery({
                    text: null,
                    position: {
                        latitude: contextMenuLocation?.latitude || 0,
                        longitude: contextMenuLocation?.longitude || 0,
                    },
                    radius: searchRadius,
                    zoom: Math.floor(mapRef.current.getZoom())
                })
                history.push('/search');
                setContextMenuLocation(null);
            }}>What's Here?</MenuButton>
            {!measureState && (
                <MenuButton
                    onClick={() => {
                        rulerControlRef.current.measuringOn();
                        rulerControlRef.current.mapClickListener({
                            lngLat: {
                                lng:contextMenuLocation?.longitude || 0,
                                lat:contextMenuLocation?.latitude || 0
                            }
                        });
                        setContextMenuLocation(null);
                    }}
                >
                Measure from Here
            </MenuButton>
            )}
            {measureState && (
                <MenuButton
                    onClick={() => {
                        rulerControlRef.current.mapClickListener({
                            lngLat: {
                                lng:contextMenuLocation?.longitude || 0,
                                lat:contextMenuLocation?.latitude || 0
                            }
                        });
                        setContextMenuLocation(null);
                    }}
                >
                Add Measure Point
            </MenuButton>
            )}
            {measureState && (
                <MenuButton
                    color="red"
                    onClick={() => {
                        rulerControlRef.current.measuringOff();
                        setContextMenuLocation(null);
                    }}
                >
                Stop Measuring
            </MenuButton>
            )}
            {/* <MenuButton
                onClick={() => {
                    setMarkers([
                        ...markers,
                        {
                            id: uuidv4().toString(),
                            name: 'New Marker',
                            latitude: contextMenuLocation?.latitude || 0,
                            longitude: contextMenuLocation?.longitude || 0,
                        },
                    ]);
                    setContextMenuLocation(null);
                }}
            >
                Add Destination Here
            </MenuButton> */}
            <MenuButton
                onClick={() => {
                    if (routeInputWaypoints.length < 2) {
                        setRouteResults({
                            loading: true
                        });
                    }
                    setRouteInputWaypoints((old) => {
                        return [
                            ...old,
                            {
                                id: uuidv4().toString(),
                                latitude: contextMenuLocation?.latitude || 0,
                                longitude: contextMenuLocation?.longitude || 0,
                            }
                        ]
                    });
                    history.push('/routes' + history.location.search);
                    setContextMenuLocation(null);
                }}>
                Add Route Point
            </MenuButton>
            {showUserReports && (
                <Box w="100%" h="1px" bg="rgba(0,0,0,0.1)"></Box>
            )}
            {showUserReports && !reportIssueState && (
                <MenuButton
                    icon="warning-sign"
                    onClick={() => {
                        if (rulerControlRef.current?.isMeasuring) {
                            rulerControlRef.current.measuringOff();
                        }
                        setReportIssueState({
                            latitude: contextMenuLocation?.latitude || 0,
                            longitude: contextMenuLocation?.longitude || 0,
                            category: 'hazard',
                        });
                        setContextMenuLocation(null);
                    }}
                >
                    Report Issue
                </MenuButton>
            )}
            {showUserReports && reportIssueState && (
                <MenuButton
                    color="red"
                    onClick={() => {
                        setReportIssueState(null);
                        setContextMenuLocation(null);
                    }}
                >
                    Cancel Report
                </MenuButton>
            )}
        </Box>
    );
}
